/**
 * useFilterParams — bidirectional URL ↔ StackFilter synchronisation hook.
 *
 * Reads and writes Application/Environment filter values via React Router's
 * useSearchParams so that the current filter state is reflected in the URL
 * and survives page refresh / can be bookmarked (BR-U5-15).
 *
 * URL format:
 *   ?application=my-app&environment=production
 *   ?application=my-app          (environment not filtered)
 *   ?environment=staging         (application not filtered)
 *   (no params)                  (no filters active — show all stacks)
 *
 * A null value in StackFilter means "no filter" (show all) and maps to the
 * param being absent from the URL (not the string "null").
 */
import { useSearchParams } from 'react-router-dom';
import type { StackFilter } from '@/types';

/**
 * Returns the current URL-persisted filter state and a setter that updates the URL.
 *
 * @returns [filters, setFilters]
 *   - filters: current StackFilter derived from URL search params
 *   - setFilters: replaces URL search params with the given filter values;
 *                 null fields are omitted from the URL
 */
export function useFilterParams(): [StackFilter, (f: StackFilter) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: StackFilter = {
    application: searchParams.get('application'),
    environment: searchParams.get('environment'),
  };

  const setFilters = (f: StackFilter): void => {
    const params: Record<string, string> = {};
    if (f.application !== null && f.application !== '') {
      params.application = f.application;
    }
    if (f.environment !== null && f.environment !== '') {
      params.environment = f.environment;
    }
    setSearchParams(params);
  };

  return [filters, setFilters];
}
